import type { ReactNode } from 'react';

const DEFAULT_SNIPPET_MAX_LENGTH = 120;
const SNIPPET_CONTEXT_LENGTH = 36;
const KEYWORD_SPLIT_REGEX = /[\s,，。！？!?；;、|/\\()[\]{}"'`<>:：\-_.]+/;
const FILE_EXTENSION_SUFFIX_REGEX =
  /\.(docx?|xlsx?|pptx?|pdf|txt|md|csv|json|xml|html?)$/i;
const HTML_ENTITY_MAP: Record<string, string> = {
  '&nbsp;': ' ',
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
};
const BLOCK_NODE_TYPES = new Set([
  'paragraph',
  'heading',
  'blockquote',
  'codeBlock',
  'listItem',
  'tableRow',
]);

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function includesCjk(value: string): boolean {
  return /[\u3400-\u9FFF]/.test(value);
}

function isAsciiWord(value: string): boolean {
  return /^[a-z0-9]+$/i.test(value);
}

function buildHighlightTerms(keywords: string[]): string[] {
  const terms = new Set<string>();

  keywords.forEach((keyword) => {
    const trimmed = keyword.trim();
    if (!trimmed) return;
    terms.add(trimmed);
  });

  return Array.from(terms).sort((a, b) => b.length - a.length);
}

function findFirstMatchIndex(text: string, terms: string[]): number {
  const lowerText = text.toLowerCase();
  const positions = terms
    .map((term) => lowerText.indexOf(term.toLowerCase()))
    .filter((index) => index >= 0)
    .sort((a, b) => a - b);

  return positions.length > 0 ? positions[0] : -1;
}

function stripHtmlTags(value: string): string {
  return value
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|tr|table|thead|tbody|tfoot|h[1-6]|caption)>/gi, '\n')
    .replace(/<(td|th)\b[^>]*>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&(nbsp|amp|lt|gt|quot|#39);/gi, (entity) => {
      const normalized = entity.toLowerCase();
      return HTML_ENTITY_MAP[normalized] ?? ' ';
    });
}

export function sanitizeSearchText(value: string): string {
  if (!value) return '';
  return stripHtmlTags(value);
}

export function extractSearchKeywords(query: string): string[] {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) return [];

  const normalizedQuery = trimmedQuery.replace(FILE_EXTENSION_SUFFIX_REGEX, '');
  const normalizedTokens = normalizedQuery
    .split(KEYWORD_SPLIT_REGEX)
    .map((keyword) => keyword.trim())
    .filter(Boolean);

  const keywordSet = new Set<string>();
  normalizedTokens.forEach((token) => {
    keywordSet.add(token);
    const fineGrained: string[] =
      token.match(/[a-z0-9]+|[\u3400-\u9FFF]+/gi) ?? [];
    fineGrained.forEach((part) => {
      const segment = part.trim();
      if (!segment) return;
      if (isAsciiWord(segment) && segment.length >= 2) {
        keywordSet.add(segment);
        return;
      }
      if (includesCjk(segment) && segment.length >= 2) {
        keywordSet.add(segment);
        // 中文短词回退：支持“测试”命中“预测”等单字历史检索行为。
        if (segment.length <= 6) {
          Array.from(segment).forEach((char) => {
            keywordSet.add(char);
          });
        }
      }
    });
  });

  if (keywordSet.size === 0 && normalizedQuery) {
    keywordSet.add(normalizedQuery);
  }

  return Array.from(keywordSet).slice(0, 12);
}

export function buildRelevantSnippet(text: string, keywords: string[]): string {
  const normalizedText = sanitizeSearchText(text).replace(/\s+/g, ' ').trim();
  if (!normalizedText) return '';

  const highlightTerms = buildHighlightTerms(keywords);

  if (keywords.length === 0) {
    return normalizedText.length > DEFAULT_SNIPPET_MAX_LENGTH
      ? `${normalizedText.slice(0, DEFAULT_SNIPPET_MAX_LENGTH)}...`
      : normalizedText;
  }

  const firstMatchIndex = findFirstMatchIndex(normalizedText, highlightTerms);

  if (firstMatchIndex < 0) {
    return normalizedText.length > DEFAULT_SNIPPET_MAX_LENGTH
      ? `${normalizedText.slice(0, DEFAULT_SNIPPET_MAX_LENGTH)}...`
      : normalizedText;
  }

  const start = Math.max(0, firstMatchIndex - SNIPPET_CONTEXT_LENGTH);
  const end = Math.min(normalizedText.length, start + DEFAULT_SNIPPET_MAX_LENGTH);
  const prefix = start > 0 ? '...' : '';
  const suffix = end < normalizedText.length ? '...' : '';
  return `${prefix}${normalizedText.slice(start, end)}${suffix}`;
}

export function buildRelevantSnippetFromSources(
  sources: Array<string | null | undefined>,
  keywords: string[],
): string {
  const normalizedSources = sources
    .map((source) => sanitizeSearchText(source || '').replace(/\s+/g, ' ').trim())
    .filter(Boolean);

  if (normalizedSources.length === 0) {
    return '';
  }

  if (keywords.length === 0) {
    return buildRelevantSnippet(normalizedSources[0], keywords);
  }

  const highlightTerms = buildHighlightTerms(keywords);
  let chosenText: string | null = null;
  let chosenMatchIndex = Number.MAX_SAFE_INTEGER;

  normalizedSources.forEach((text) => {
    const matchIndex = findFirstMatchIndex(text, highlightTerms);
    if (matchIndex < 0) return;

    if (!chosenText || matchIndex < chosenMatchIndex) {
      chosenText = text;
      chosenMatchIndex = matchIndex;
    }
  });

  return buildRelevantSnippet(chosenText || normalizedSources[0], keywords);
}

export function renderHighlightedText(
  text: string,
  keywords: string[],
  options?: { fallbackLeadingChars?: number; enableLooseMatching?: boolean },
): ReactNode {
  if (!text || keywords.length === 0) return text;

  const ranges = collectHighlightRanges(
    text,
    keywords,
    Boolean(options?.enableLooseMatching),
  );
  if (ranges.length > 0) {
    return renderByRanges(text, ranges);
  }

  if (!options?.fallbackLeadingChars) {
    return text;
  }

  const leadingChars = Math.min(options.fallbackLeadingChars, text.length);
  if (leadingChars <= 0) return text;

  return (
    <>
      <mark className="bg-transparent px-0 font-semibold text-[#3370FF]">
        {text.slice(0, leadingChars)}
      </mark>
      {text.slice(leadingChars)}
    </>
  );
}

function collectHighlightRanges(
  text: string,
  keywords: string[],
  enableLooseMatching: boolean,
): Array<[number, number]> {
  const terms = buildHighlightTerms(keywords);
  if (terms.length === 0) {
    return [];
  }

  const ranges: Array<[number, number]> = [];
  const exactMatchedTerms = new Set<string>();

  terms.forEach((term) => {
    if (!term) return;
    const regex = new RegExp(escapeRegExp(term), 'gi');
    let matched = false;
    let match = regex.exec(text);
    while (match) {
      const start = match.index;
      const end = start + match[0].length;
      ranges.push([start, end]);
      matched = true;
      if (match[0].length === 0) {
        regex.lastIndex += 1;
      }
      match = regex.exec(text);
    }
    if (matched) {
      exactMatchedTerms.add(term.toLowerCase());
    }
  });

  if (!enableLooseMatching) {
    return mergeRanges(ranges);
  }

  terms.forEach((term) => {
    if (!term || exactMatchedTerms.has(term.toLowerCase())) {
      return;
    }
    const looseRange = findLooseMatchRange(text, term);
    if (!looseRange) {
      return;
    }
    ranges.push(looseRange);
  });

  return mergeRanges(ranges);
}

function renderByRanges(text: string, ranges: Array<[number, number]>): ReactNode {
  if (ranges.length === 0) {
    return text;
  }

  const nodes: ReactNode[] = [];
  let cursor = 0;

  ranges.forEach(([start, end], index) => {
    if (start > cursor) {
      nodes.push(text.slice(cursor, start));
    }
    nodes.push(
      <mark
        key={`hl-${start}-${end}-${index}`}
        className="bg-transparent px-0 font-semibold text-[#1456F0]"
      >
        {text.slice(start, end)}
      </mark>,
    );
    cursor = end;
  });

  if (cursor < text.length) {
    nodes.push(text.slice(cursor));
  }

  return <>{nodes}</>;
}

function mergeRanges(ranges: Array<[number, number]>): Array<[number, number]> {
  if (ranges.length <= 1) {
    return ranges;
  }

  const sorted = [...ranges].sort((a, b) => a[0] - b[0]);
  const merged: Array<[number, number]> = [sorted[0]];

  for (let i = 1; i < sorted.length; i += 1) {
    const current = sorted[i];
    const last = merged[merged.length - 1];
    if (current[0] <= last[1]) {
      last[1] = Math.max(last[1], current[1]);
      continue;
    }
    merged.push(current);
  }

  return merged;
}

function findLooseMatchRange(text: string, keyword: string): [number, number] | null {
  const compact = buildCompactIndexMap(text);
  if (!compact.compactText) {
    return null;
  }

  const compactTerm = normalizeLooseText(keyword);
  if (compactTerm.length < 2) {
    return null;
  }

  const startInCompact = compact.compactText.indexOf(compactTerm);
  if (startInCompact < 0) {
    return null;
  }
  const endInCompact = startInCompact + compactTerm.length - 1;
  const startInOriginal = compact.indices[startInCompact];
  const endInOriginal = compact.indices[endInCompact];
  if (startInOriginal == null || endInOriginal == null || endInOriginal < startInOriginal) {
    return null;
  }
  return [startInOriginal, endInOriginal + 1];
}

function normalizeLooseText(value: string): string {
  return value
    .toLowerCase()
    .replace(/[\s\-_./\\]+/g, '')
    .trim();
}

function buildCompactIndexMap(text: string): { compactText: string; indices: number[] } {
  const indices: number[] = [];
  const chars: string[] = [];

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (/[a-z0-9\u3400-\u9FFF]/i.test(ch)) {
      chars.push(ch.toLowerCase());
      indices.push(i);
    }
  }

  return {
    compactText: chars.join(''),
    indices,
  };
}

export function hasKeywordMatchInText(text: string, keywords: string[]): boolean {
  const normalizedText = sanitizeSearchText(text || '').replace(/\s+/g, ' ').trim();
  if (!normalizedText || keywords.length === 0) return false;

  const terms = buildHighlightTerms(keywords);
  if (terms.length === 0) return false;

  const lowerText = normalizedText.toLowerCase();
  return terms.some((term) => lowerText.includes(term.toLowerCase()));
}

export function hasKeywordMatchInSources(
  sources: Array<string | null | undefined>,
  keywords: string[],
): boolean {
  if (keywords.length === 0) return true;
  return sources.some((source) => hasKeywordMatchInText(source || '', keywords));
}

function getMatchStats(
  text: string,
  keywords: string[],
): { matchedTerms: number; firstIndex: number } {
  const normalizedText = sanitizeSearchText(text || '').replace(/\s+/g, ' ').trim();
  if (!normalizedText || keywords.length === 0) {
    return { matchedTerms: 0, firstIndex: Number.MAX_SAFE_INTEGER };
  }

  const lowerText = normalizedText.toLowerCase();
  const terms = buildHighlightTerms(keywords);
  let matchedTerms = 0;
  let firstIndex = Number.MAX_SAFE_INTEGER;

  terms.forEach((term) => {
    const idx = lowerText.indexOf(term.toLowerCase());
    if (idx >= 0) {
      matchedTerms += 1;
      if (idx < firstIndex) {
        firstIndex = idx;
      }
    }
  });

  return { matchedTerms, firstIndex };
}

export type SearchSortableItem = {
  title: string;
  snippet?: string;
  previewContent?: string;
  score?: number;
};

export function sortSearchResultsByKeywordPriority<T extends SearchSortableItem>(
  items: T[],
  keywords: string[],
): T[] {
  if (items.length <= 1 || keywords.length === 0) {
    return [...items].sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
  }

  return [...items].sort((a, b) => {
    const aTitle = getMatchStats(a.title, keywords);
    const bTitle = getMatchStats(b.title, keywords);

    const aContent = getMatchStats(`${a.previewContent || ''} ${a.snippet || ''}`, keywords);
    const bContent = getMatchStats(`${b.previewContent || ''} ${b.snippet || ''}`, keywords);

    const aBucket = aTitle.matchedTerms > 0 ? 0 : aContent.matchedTerms > 0 ? 1 : 2;
    const bBucket = bTitle.matchedTerms > 0 ? 0 : bContent.matchedTerms > 0 ? 1 : 2;
    if (aBucket !== bBucket) {
      return aBucket - bBucket;
    }

    if (aBucket === 0) {
      if (aTitle.matchedTerms !== bTitle.matchedTerms) {
        return bTitle.matchedTerms - aTitle.matchedTerms;
      }
      if (aTitle.firstIndex !== bTitle.firstIndex) {
        return aTitle.firstIndex - bTitle.firstIndex;
      }
    } else if (aBucket === 1) {
      if (aContent.matchedTerms !== bContent.matchedTerms) {
        return bContent.matchedTerms - aContent.matchedTerms;
      }
      if (aContent.firstIndex !== bContent.firstIndex) {
        return aContent.firstIndex - bContent.firstIndex;
      }
    }

    return (b.score ?? 0) - (a.score ?? 0);
  });
}

function walkEditorNode(node: unknown, collector: string[]): void {
  if (!node) return;

  if (Array.isArray(node)) {
    node.forEach((child) => walkEditorNode(child, collector));
    return;
  }

  if (typeof node !== 'object') return;

  const current = node as { text?: unknown; type?: unknown; content?: unknown };

  if (typeof current.text === 'string' && current.text.trim()) {
    collector.push(current.text);
  }

  walkEditorNode(current.content, collector);

  if (typeof current.type === 'string' && BLOCK_NODE_TYPES.has(current.type)) {
    collector.push('\n\n');
  }
}

function normalizePreviewText(value: string): string {
  return sanitizeSearchText(value)
    .split('\n')
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter((line, index, all) => line || all[index - 1] !== '')
    .join('\n')
    .trim();
}

export function extractPreviewTextFromArticleContent(content: string): string {
  const raw = content?.trim() || '';
  if (!raw) return '';

  try {
    const parsed = JSON.parse(raw) as unknown;
    const collector: string[] = [];
    walkEditorNode(parsed, collector);
    const normalized = normalizePreviewText(collector.join(''));
    if (normalized) return normalized;
  } catch {
    // Fallback to raw text when content is not valid editor JSON.
  }

  return normalizePreviewText(raw);
}
